import { Button, Col, Input, Row, Table } from 'antd'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  deleteBarcodes,
  getBarcodesAndSizes,
  IBarcode,
  IBarcodeAndSizes,
  IBarcodeWithExtraProps,
  InsertPublicBarcodeRequest,
  insertPublicBarcodes,
  ISize,
  updatePublicBarcodes,
  UpdatePublicBarcodesRequest,
} from '../../../Services/Kiosk/BarcodeService'
import { IColumn } from '../../../Interfaces/IColumn'
import { SaveFilled, EditOutlined, DeleteFilled } from '@ant-design/icons'
import PublicBarcodeCell from './PublicBarcodeCell'
import { setArticleBarcodesAndSizes } from '../../../Slices/Kiosk/ArticleDetailSlice'
import { retrieveSelectedArticleKey } from 'Helpers/ArticlesHelper'
import InternalBarcodeCell from './InternalBarcodeCell'
import { isProdSite } from 'Helpers/GenericHelpers'

const TIPO = 'TIPO'
const COMANDI = 'COMANDI'
const ARTICLE_ID_FOR_NEW_BARCODE = -1

// Permette di scegliere dove posizionare i comandi per entrare nelle modalità di edit e delete dei barcode.
const useCommandsOnColumn = false

export default function Barcodes() {
  const dispatch = useAppDispatch()
  const workspaceImagesVisible = useAppSelector(state => state.present.configuration.workspaceImagesVisible)

  const barcodesAndSizes: IBarcodeAndSizes = useAppSelector(
    state => state.present.articleDetail.articleSelected.barcodesAndSizes,
  )
  const [clonedBarcodes, setClonedBarcodes] = useState<IBarcodeWithExtraProps[]>([])
  const [sizes, setSizes] = useState<ISize[]>([])
  const articleIds = useAppSelector(state => state.present.article.rowKeysSelected)
  const [columns, setColumns] = useState([])
  const [rows, setRows] = useState([])
  const [isDeleteMode, setIsDeleteMode] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)

  function handleOnCLickDelete(size: string, value: string) {
    const barcode = clonedBarcodes.find(x => x.size.sizeName === size && x.value === value)
    if (barcode) {
      barcode.isDeleted = !barcode.isDeleted
      setClonedBarcodes([...clonedBarcodes])
    }
  }

  function handleOnBarcodeChanged(sizename: string, originalBarcode: string, newBarcode: string) {
    const barcode = clonedBarcodes.find(x => x.size.sizeName === sizename && x.originalValue === originalBarcode)
    if (barcode) {
      barcode.value = newBarcode
      setClonedBarcodes([...clonedBarcodes])
    }
  }

  async function saveChanges() {
    const barcodeNamesToDelete = clonedBarcodes.filter(x => x.isDeleted).map(x => x.value)
    if (barcodeNamesToDelete.length > 0) {
      await dispatch(deleteBarcodes(barcodeNamesToDelete))
    }

    const barcodesToEdit = clonedBarcodes.filter(
      x => x.value != x.originalValue && x.variant !== ARTICLE_ID_FOR_NEW_BARCODE,
    )
    if (barcodesToEdit.length > 0) {
      const request: UpdatePublicBarcodesRequest = {
        barcodes: barcodesToEdit.map(x => {
          return { oldValue: x.originalValue, newValue: x.value }
        }),
      }
      await dispatch(updatePublicBarcodes(request))
    }

    const articleId = retrieveSelectedArticleKey(articleIds)

    const barcodesToAdd = clonedBarcodes.filter(
      x => x.value != x.originalValue && x.variant === ARTICLE_ID_FOR_NEW_BARCODE,
    )
    if (barcodesToAdd.length > 0) {
      const request: InsertPublicBarcodeRequest = {
        barcodesAndSizeIds: barcodesToAdd.map(x => {
          return { barcode: x.value, sizeId: x.size.sizeId }
        }),
      }
      await dispatch(insertPublicBarcodes(articleId, request))
    }

    if (articleId) {
      const result = await getBarcodesAndSizes(articleId)
      dispatch(setArticleBarcodesAndSizes(result))
      setIsEditMode(false)
      setIsDeleteMode(false)
    }
  }

  function getColumnNames(sizes: ISize[]) {
    if (useCommandsOnColumn) {
      return [TIPO, ...sizes.map(x => x.sizeName), COMANDI]
    } else {
      return [TIPO, ...sizes.map(x => x.sizeName)]
    }
  }

  function mapColumns(columnNames: string[], numberOfPublicRows: number, numberOfInternalRows: number) {
    const columnsWidth = `${(100 / columnNames.length).toFixed(2)}%`
    return columnNames.map(sizeName => {
      const columnObject: IColumn = {
        title: sizeName,
        dataIndex: sizeName,
        key: sizeName,
        width: columnsWidth,
      }
      if (sizeName === TIPO) {
        columnObject.title = ''
      }
      if (sizeName === COMANDI) {
        columnObject.title = ''
        //columnObject.width = 5
        columnObject.onCell = (_, index) => {
          // Applico span per le righe pubbliche
          if (numberOfPublicRows > 0) {
            if (index === 0) {
              return { rowSpan: numberOfPublicRows }
            }
          }
          // Applico lo span per le righe interne
          if (index === numberOfPublicRows) {
            return { rowSpan: numberOfInternalRows }
          }
          // Azzereo tutti gli altri span
          if (index > numberOfPublicRows) {
            return { rowSpan: 0 }
          }
        }
      }
      return columnObject
    })
  }

  function mapRows(
    barcodesBySize: Map<string, IBarcodeWithExtraProps[]>,
    numberOfPublicRows: number,
    numberOfInternalRows: number,
  ): any[] {
    // Creo le righe pubbliche della tabella.
    const result = []
    for (let i = 0; i < numberOfPublicRows; i++) {
      const row = {}
      row['key'] = `public_${i}`
      row[TIPO] = 'PUBBLICI'

      // Creo tasto di modifica dei barcode pubblici
      row['COMANDI'] = (
        <Button
          onClick={() => setIsEditMode(old => !old)}
          style={{ backgroundColor: isEditMode ? 'red' : 'white' }}
          icon={<EditOutlined className='generic-icon' />}
        ></Button>
      )

      barcodesBySize.forEach((barcodes, sizeName) => {
        const barcode = barcodes.filter(x => x.isPublic)[i]
        row[sizeName] = (
          <PublicBarcodeCell
            key={i + sizeName}
            barcode={barcode}
            isEditMode={isEditMode}
            onBarcodeChanged={handleOnBarcodeChanged}
          ></PublicBarcodeCell>
        )
      })
      result.push(row)
    }

    // Creo le righe interne della tabella.
    for (let i = 0; i < numberOfInternalRows; i++) {
      const row = {}
      row['key'] = `internal_${i}`
      row[TIPO] = 'INTERNI'

      // Creo tasto di cancellazione dei barcode interni
      row['COMANDI'] = (
        <Button
          type='default'
          onClick={() => setIsDeleteMode(old => !old)}
          style={{ backgroundColor: isDeleteMode ? 'red' : 'white' }}
          icon={<DeleteFilled style={{ fontSize: '24px' }}></DeleteFilled>}
        ></Button>
      )

      barcodesBySize.forEach((barcodes, sizeName) => {
        const barcode = barcodes.filter(x => !x.isPublic)[i]
        row[sizeName] = (
          <InternalBarcodeCell
            key={i + sizeName}
            barcode={barcode}
            isDeleteMode={isDeleteMode}
            onDeleteClick={handleOnCLickDelete}
          ></InternalBarcodeCell>
        )
      })
      result.push(row)
    }

    return result
  }

  function creteBySizeBarcodeMap(barcodes: IBarcodeWithExtraProps[]) {
    // Differenzia i barcode per taglia.
    const barcodesBySize = new Map<string, IBarcodeWithExtraProps[]>()
    for (const barcode of barcodes) {
      if (!barcodesBySize.has(barcode.size.sizeName)) barcodesBySize.set(barcode.size.sizeName, [])
      barcodesBySize.get(barcode.size.sizeName).push(barcode)
    }
    return barcodesBySize
  }

  function countRows(barcodesBySize: Map<string, IBarcodeWithExtraProps[]>): [number, number] {
    // Calcola il numero di righe di barcode pubblici ed interni
    let numberOfInternalRows = 0
    let numberOfPublicRows = 0
    for (const barcodes of barcodesBySize.values()) {
      const numberOfInternalBarcodes = barcodes.filter(x => !x.isPublic).length
      const numberOfPublicBarcodes = barcodes.filter(x => x.isPublic).length
      if (numberOfInternalBarcodes > numberOfInternalRows) numberOfInternalRows = numberOfInternalBarcodes
      if (numberOfPublicBarcodes > numberOfPublicRows) numberOfPublicRows = numberOfPublicBarcodes
    }

    if (numberOfPublicRows === 0) numberOfPublicRows = 1

    return [numberOfPublicRows, numberOfInternalRows]
  }

  useEffect(() => {
    //create a clone of barcodes with extra props
    const cloned =
      barcodesAndSizes.barcodes?.map(x => {
        return { originalValue: x.value, isDeleted: false, ...x }
      }) ?? []

    // Add empty public barcodes if not any, needed for insert new public barcodes
    barcodesAndSizes.sizes?.forEach(size => {
      const publicBarcode = cloned.filter(x => x.isPublic && x.size.sizeName === size.sizeName)
      if (publicBarcode.length === 0) {
        cloned.push({
          isDeleted: false,
          isPublic: true,
          originalValue: '',
          size: size,
          value: '',
          variant: ARTICLE_ID_FOR_NEW_BARCODE,
        })
      }
    })

    setClonedBarcodes(cloned)
    const sortedSizes = [...(barcodesAndSizes.sizes ?? [])].sort((a, b) => a.sizeOrder - b.sizeOrder)
    setSizes(sortedSizes)
    setIsEditMode(false)
    setIsDeleteMode(false)
  }, [barcodesAndSizes])

  useEffect(() => {
    if (!clonedBarcodes) return

    const barcodesBySize = creteBySizeBarcodeMap(clonedBarcodes)
    const [numberOfPublicRows, numberOfInternalRows] = countRows(barcodesBySize)

    const columnNames = getColumnNames(sizes)
    setColumns(mapColumns(columnNames, numberOfPublicRows, numberOfInternalRows))

    const rows = mapRows(barcodesBySize, numberOfPublicRows, numberOfInternalRows)
    setRows(rows)
  }, [clonedBarcodes, sizes, isEditMode, isDeleteMode])

  const anyChanges = clonedBarcodes.some(x => x.isDeleted) || clonedBarcodes.some(x => x.value !== x.originalValue)
  return workspaceImagesVisible ? (
    <div>
      <Row>
        <Col span={1} />
        {!isProdSite() ? (
          <Col span={23}>
            <SaveFilled
              className={'generic-icon' + (anyChanges ? ' active' : '')}
              onClick={anyChanges ? saveChanges : null}
            />
            {!useCommandsOnColumn ? (
              <>
                <EditOutlined
                  className={'generic-icon' + (isEditMode ? ' active' : '')}
                  onClick={() => setIsEditMode(old => !old)}
                />
                <DeleteFilled
                  className={'generic-icon' + (isDeleteMode ? ' active' : '')}
                  onClick={() => setIsDeleteMode(old => !old)}
                ></DeleteFilled>
              </>
            ) : null}
          </Col>
        ) : null}
      </Row>
      <Row>
        <Col span={1}> </Col>
        <Col span={22}>
          <Table
            id='barcodes-table'
            style={{ width: '100%' }}
            columns={columns}
            dataSource={rows}
            pagination={false}
          ></Table>
        </Col>
        <Col span={1} />
      </Row>
    </div>
  ) : null
}
